/**
 * JobApplications.jsx — Şub 10, 2026
 *
 * Admin panel — firma scope'lu iş başvuruları listesi + detay modal.
 * - Listede arama (ad/email), durum ve pozisyon filtreleri.
 * - Detayda CV/motivasyon mektubu indirilebilir.
 * - Durum güncelleme (NEW/REVIEWING/INTERVIEW/HIRED/REJECTED) ve admin notu.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Briefcase, Download, Loader2, Search, Trash2, X, ChevronRight, Filter,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const STATUSES = [
  { value: 'NEW',        label: 'Yeni',          badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  { value: 'REVIEWING',  label: 'İncelemede',    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  { value: 'INTERVIEW',  label: 'Mülakat',       badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' },
  { value: 'HIRED',      label: 'İşe Alındı',    badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' },
  { value: 'REJECTED',   label: 'Reddedildi',    badge: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300' },
];

const formatDate = (s) => {
  if (!s) return '-';
  try {
    return new Date(s).toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return s; }
};

const statusOf = (v) => STATUSES.find((x) => x.value === v) || STATUSES[0];

export default function JobApplications() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [functieFilter, setFunctieFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [total, setTotal] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (statusFilter) params.set('status', statusFilter);
      if (functieFilter) params.set('functie', functieFilter);
      const r = await api.get(`/job-applications?${params.toString()}`);
      if (r.data?.success) {
        setItems(r.data.data.items || []);
        setCounts(r.data.data.counts || {});
        setTotal(r.data.data.total || 0);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Liste alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, functieFilter]);

  const onSearch = (e) => { e.preventDefault(); load(); };

  const functies = useMemo(
    () => Array.from(new Set(items.map((i) => i.functie).filter(Boolean))).sort(),
    [items],
  );

  const refreshAfterChange = (updated) => {
    setItems((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
    if (selected?.id === updated.id) setSelected({ ...selected, ...updated });
  };

  const removeFromList = (id) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
    if (selected?.id === id) setSelected(null);
  };

  return (
    <div className="space-y-4" data-testid="job-applications-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <Briefcase className="h-5 w-5 text-blue-700 dark:text-blue-300" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">İş Başvuruları</h1>
            <p className="text-xs text-slate-500">Toplam {total} başvuru</p>
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {STATUSES.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(statusFilter === s.value ? '' : s.value)}
            className={`text-left p-3 rounded-xl border transition ${
              statusFilter === s.value
                ? 'border-blue-500 ring-2 ring-blue-500/30 bg-blue-50 dark:bg-blue-900/20'
                : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300'
            }`}
            data-testid={`ja-stat-${s.value}`}
          >
            <p className="text-xs text-slate-500 dark:text-slate-400">{s.label}</p>
            <p className="text-xl font-bold text-slate-900 dark:text-slate-100">{counts[s.value] || 0}</p>
          </button>
        ))}
      </div>

      {/* Filters */}
      <form onSubmit={onSearch} className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Ad, soyad, e-posta veya telefon ara…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm"
            data-testid="ja-search-input"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-sm font-medium"
          data-testid="ja-search-btn"
        >
          Ara
        </button>
        {functies.length > 0 && (
          <select
            value={functieFilter}
            onChange={(e) => setFunctieFilter(e.target.value)}
            className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm"
            data-testid="ja-functie-filter"
          >
            <option value="">Tüm pozisyonlar</option>
            {functies.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        )}
        {(statusFilter || functieFilter || search) && (
          <button
            type="button"
            onClick={() => { setSearch(''); setStatusFilter(''); setFunctieFilter(''); }}
            className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-sm text-slate-700 dark:text-slate-200 inline-flex items-center gap-1.5"
            data-testid="ja-clear-filters"
          >
            <Filter className="h-3.5 w-3.5" /> Temizle
          </button>
        )}
      </form>

      {/* List */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">Başvuru bulunamadı</div>
        ) : (
          <div className="divide-y divide-slate-200 dark:divide-slate-700">
            {items.map((a) => {
              const st = statusOf(a.status);
              return (
                <button
                  key={a.id}
                  onClick={() => setSelected(a)}
                  className="w-full text-left p-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition flex items-center gap-3"
                  data-testid={`ja-row-${a.id}`}
                >
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white flex items-center justify-center font-semibold text-sm flex-shrink-0">
                    {(a.voornaam?.[0] || '?').toUpperCase()}{(a.achternaam?.[0] || '').toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-slate-900 dark:text-slate-100">
                        {a.voornaam} {a.achternaam}
                      </span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${st.badge}`}>{st.label}</span>
                    </div>
                    <p className="text-xs text-slate-500 truncate">
                      {a.functie || '-'} · {a.email} · {formatDate(a.created_at)}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selected && (
        <DetailModal
          application={selected}
          onClose={() => setSelected(null)}
          onUpdated={refreshAfterChange}
          onDeleted={removeFromList}
        />
      )}
    </div>
  );
}

function DetailModal({ application, onClose, onUpdated, onDeleted }) {
  const [a, setA] = useState(application);
  const [note, setNote] = useState(application.admin_note || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Detayı yeniden çek (eklerin meta'sı vb. için)
    (async () => {
      try {
        const r = await api.get(`/job-applications/${application.id}`);
        if (r.data?.success) {
          setA(r.data.data);
          setNote(r.data.data.admin_note || '');
        }
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line
  }, [application.id]);

  const changeStatus = async (status) => {
    setSaving(true);
    try {
      const r = await api.patch(`/job-applications/${a.id}`, { status });
      if (r.data?.success) {
        toast.success('Durum güncellendi');
        const next = { ...a, ...r.data.data };
        setA(next); onUpdated(next);
      }
    } catch (err) { toast.error(err?.response?.data?.detail || 'Güncellenemedi'); }
    finally { setSaving(false); }
  };

  const saveNote = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/job-applications/${a.id}`, { admin_note: note });
      if (r.data?.success) { toast.success('Not kaydedildi'); onUpdated({ ...a, admin_note: note }); }
    } catch (err) { toast.error(err?.response?.data?.detail || 'Kaydedilemedi'); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm('Bu başvuruyu kalıcı olarak silmek istediğinize emin misiniz?')) return;
    setSaving(true);
    try {
      await api.delete(`/job-applications/${a.id}`);
      toast.success('Başvuru silindi');
      onDeleted(a.id);
      onClose();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Silinemedi'); }
    finally { setSaving(false); }
  };

  const downloadFile = async (kind) => {
    try {
      const r = await api.get(`/job-applications/${a.id}/file/${kind}`, { responseType: 'blob' });
      const att = (a.attachments || []).find((x) => x.kind === kind);
      const filename = att?.filename || `${kind}.bin`;
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url; link.download = filename;
      document.body.appendChild(link); link.click();
      link.remove(); window.URL.revokeObjectURL(url);
    } catch (err) { toast.error('İndirilemedi'); }
  };

  const cv = (a.attachments || []).find((x) => x.kind === 'cv');
  const brief = (a.attachments || []).find((x) => x.kind === 'motivatiebrief');
  const st = statusOf(a.status);

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-slate-900 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="ja-detail-modal"
      >
        {/* Header */}
        <div className="p-5 border-b border-slate-200 dark:border-slate-700 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
                {a.voornaam} {a.achternaam}
              </h2>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${st.badge}`}>{st.label}</span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">{a.functie || '-'} · {formatDate(a.created_at)}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="ja-detail-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-5 space-y-5">
          {/* Status switcher */}
          <div>
            <p className="text-xs font-semibold text-slate-500 mb-2">Durum değiştir</p>
            <div className="flex flex-wrap gap-1.5">
              {STATUSES.map((s) => (
                <button
                  key={s.value}
                  disabled={saving || a.status === s.value}
                  onClick={() => changeStatus(s.value)}
                  className={`text-xs px-3 py-1.5 rounded-full font-medium border ${
                    a.status === s.value
                      ? 'border-blue-500 ring-2 ring-blue-500/20 ' + s.badge
                      : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`ja-set-${s.value}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Persoonlijk */}
          <Section title="Persoonlijke gegevens">
            <KV k="E-posta" v={<a href={`mailto:${a.email}`} className="text-blue-700 hover:underline">{a.email}</a>} />
            <KV k="Telefon" v={<a href={`tel:${a.telefoon}`} className="text-blue-700 hover:underline">{a.telefoon}</a>} />
            <KV k="Doğum tarihi" v={a.geboortedatum} />
            <KV k="Uyruk" v={a.nationaliteit} />
            <KV k="Şehir/Ülke" v={a.woonplaats} />
          </Section>

          <Section title="Functie & beschikbaarheid">
            <KV k="Pozisyon" v={a.functie} />
            <KV k="Müsaitlik" v={a.beschikbaar} />
          </Section>

          {(a.rijbewijs?.length || a.talen?.length || a.diploma || a.ervaring || a.werkgevers) && (
            <Section title="Niteleyici Bilgiler">
              {a.rijbewijs?.length > 0 && <KV k="Ehliyet/Belge" v={a.rijbewijs.join(', ')} />}
              <KV k="Diploma" v={a.diploma} />
              <KV k="Deneyim" v={a.ervaring} />
              {a.talen?.length > 0 && <KV k="Diller" v={a.talen.join(', ')} />}
              {a.werkgevers && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 mb-1">Önceki işverenler</p>
                  <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap bg-slate-50 dark:bg-slate-800 p-2 rounded">{a.werkgevers}</p>
                </div>
              )}
            </Section>
          )}

          <Section title="Motivasyon">
            <p className="text-sm text-slate-800 dark:text-slate-200 whitespace-pre-wrap bg-slate-50 dark:bg-slate-800 p-3 rounded-lg">
              {a.motivatie || '-'}
            </p>
            <KV k="Kaynak" v={a.bron} />
          </Section>

          {/* Attachments */}
          {(cv || brief) && (
            <Section title="Belgeler">
              <div className="flex flex-wrap gap-2">
                {cv && (
                  <button
                    onClick={() => downloadFile('cv')}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
                    data-testid="ja-download-cv"
                  >
                    <Download className="h-4 w-4" /> CV ({cv.filename || 'cv'})
                  </button>
                )}
                {brief && (
                  <button
                    onClick={() => downloadFile('motivatiebrief')}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
                    data-testid="ja-download-brief"
                  >
                    <Download className="h-4 w-4" /> Motivasyon Mektubu ({brief.filename || 'brief'})
                  </button>
                )}
              </div>
            </Section>
          )}

          {/* Admin note */}
          <Section title="Yönetici Notu">
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Mülakat notu, takip tarihi vs."
              className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm"
              data-testid="ja-admin-note"
            />
            <div className="flex justify-end">
              <button
                onClick={saveNote}
                disabled={saving || note === (a.admin_note || '')}
                className="px-4 py-1.5 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-xs font-medium disabled:opacity-60"
                data-testid="ja-save-note"
              >
                Notu Kaydet
              </button>
            </div>
          </Section>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <button
            onClick={remove}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-900/20 text-sm"
            data-testid="ja-delete-btn"
          >
            <Trash2 className="h-4 w-4" /> Sil
          </button>
          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-sm"
            data-testid="ja-close-btn"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-2">
      <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">{title}</h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function KV({ k, v }) {
  if (v == null || v === '') return null;
  return (
    <div className="flex items-start gap-2 text-sm">
      <span className="text-slate-500 dark:text-slate-400 min-w-[110px]">{k}:</span>
      <span className="text-slate-800 dark:text-slate-100 flex-1 break-words">{v}</span>
    </div>
  );
}
